import React, { FC, useRef, useState } from 'react';
import { useDrag, useDrop, XYCoord } from 'react-dnd';

type Identifier = string | symbol;
import clsx from 'clsx';
import styles from './DraggableRow.module.css';
import { BlazeTableColDef } from '../BlazeTable.types';
import BzmIcon from '../../icons/BzmIcon';
import CheckBox from '../../ui/checkBox/CheckBox';
import BlazeTableCell from '../BlazeTableCell';
import _ from 'lodash';

export const ItemTypes = {
  TABLE_ITEM: 'tableItem',
};

export interface DraggableRowProps {
  item: any;
  index: number;
  moveItem: (dragIndex: number, hoverIndex: number) => void;
  onMoveFinished: () => void;
  columnDef: BlazeTableColDef[];
  onSelect: (id: number) => void;
  isSelected: boolean;
  expandableComponent?: (item: any) => any;
}

interface DragItem {
  index: number;
  id: string;
  type: string;
}

export const DraggableRow: FC<DraggableRowProps> = ({
  item,
  index,
  moveItem,
  onMoveFinished,
  onSelect,
  columnDef,
  isSelected,
  expandableComponent,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isDraggable, setIsDraggable] = useState(false);
  const [{ handlerId }, drop] = useDrop<DragItem, void, { handlerId: Identifier | null }>({
    accept: ItemTypes.TABLE_ITEM,

    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item: DragItem, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }

      // Determine rectangle on screen
      const hoverBoundingRect = ref.current?.getBoundingClientRect();

      // Get vertical middle
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

      // Determine mouse position
      const clientOffset = monitor.getClientOffset();

      // Get pixels to the top
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%

      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      // Time to actually perform the action
      moveItem(dragIndex, hoverIndex);

      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.TABLE_ITEM,
    item: () => {
      return { id: item.id, index };
    },
    collect: (monitor: any) => ({
      isDragging: monitor.isDragging(),
    }),
    end: (item, monitor) => {
      onMoveFinished();
      setIsDraggable(false);
    },
    canDrag: isDraggable,
  });

  drag(drop(ref));

  const renderTableCells = (item: any) =>
    columnDef.map((columnDefinition: BlazeTableColDef, index: number) => (
      <BlazeTableCell
        key={'cell-' + item.id + '-i-' + index}
        item={item}
        columnDefinition={columnDefinition}
        index={index}
      />
    ));

  const getDragCell = (id: number) => {
    const colSize = 120;
    return (
      <div className={clsx('flex-table-col', 'flex-table-col-' + colSize)}>
        <span
          title="Change order"
          className={'position-relative mr-4'}
          style={{ bottom: '-1px' }}
          onMouseDown={() => setIsDraggable(true)}
        >
          <BzmIcon size={20} style={{ cursor: 'move' }} variant={'icon-bzm-drag-vertical'} color={'#7f7f7f'} />
        </span>

        {!_.isNil(expandableComponent) && (
          <button
            onClick={() => {
              setIsCollapsed(!isCollapsed);
            }}
            className={'btn btn-link btn-no-hover flex-table-collapse-control mr-4 position-relative'}
            style={{
              bottom: '2px',
            }}
          >
            <BzmIcon variant={clsx(!isCollapsed ? 'icon-bzm-chevron-right' : 'icon-bzm-chevron-down', 'icon-sm')} />
          </button>
        )}
        <CheckBox
          style={{ bottom: '-1px' }}
          className={'position-relative'}
          onCheck={() => onSelect(id)}
          checked={isSelected}
        />
      </div>
    );
  };

  if (isDragging) {
    return (
      <div className={clsx('flex-table-item', styles.droppingPlaceholder)} ref={ref} data-handler-id={handlerId}>
        <p className={'text-center mb-0'}>+Drag & Drop item.</p>
      </div>
    );
  } else {
    return (
      <div
        className={clsx('flex-table-item', isCollapsed && 'flex-table-item-expanded')}
        ref={ref}
        data-handler-id={handlerId}
      >
        <div className={clsx('flex-table-item-heading', styles.noHover)}>
          <div className="flex-table-row">
            {getDragCell(item.id)}
            {renderTableCells(item)}
          </div>
        </div>
        {/* Collapsable component */}
        {isCollapsed && (
          <div className={'flex-table-item-expanded'}>{expandableComponent && expandableComponent(item)}</div>
        )}
      </div>
    );
  }
};
